"use client";

import React, { useState } from "react";
import { Play, MapPin, X } from "lucide-react";
import { Dialog, DialogContent, DialogClose, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
interface VideoItem {
  id: string;
  title: string;
  location: string;
  thumbnail: string;
  youtubeId: string;
  aspect: "landscape" | "portrait";
  description: string;
}
const VIDEOS: VideoItem[] = [{
  id: "v1",
  title: "Dense Underbrush Annihilation",
  location: "Gregg County, TX",
  thumbnail: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569013/img/mulching_action_landscape_1.png",
  youtubeId: "dQw4w9WgXcQ",
  aspect: "landscape",
  description: "Clearing 5 acres of heavy yaupon and briars with the Kubota SVL97-2."
}, {
  id: "v2",
  title: "Tree Eater in Action",
  location: "Harrison County, TX",
  thumbnail: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569013/img/mulching_action_portrait_1.png",
  youtubeId: "dQw4w9WgXcQ",
  aspect: "portrait",
  description: "Vertical view of the forestry mulcher taking down a 10-inch pine."
}, {
  id: "v3",
  title: "Pasture Reclamation",
  location: "Rusk County, TX",
  thumbnail: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569013/img/mulching_action_landscape_2.png",
  youtubeId: "dQw4w9WgXcQ",
  aspect: "landscape",
  description: "Reclaiming overgrown pasture land for cattle grazing."
}, {
  id: "v4",
  title: "Right-of-Way Clearing",
  location: "Smith County, TX",
  thumbnail: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569013/img/mulching_action_portrait_2.png",
  youtubeId: "dQw4w9WgXcQ",
  aspect: "portrait",
  description: "Precision clearing along fence lines and utility easements."
}, {
  id: "v5",
  title: "Stump Grinding Finale",
  location: "Upshur County, TX",
  thumbnail: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569013/img/mulching_action_landscape_3.png",
  youtubeId: "dQw4w9WgXcQ",
  aspect: "landscape",
  description: "Final cleanup and grading after a major land clearing project."
}, {
  id: "v6",
  title: "Cedar Thicket Removal",
  location: "Panola County, TX",
  thumbnail: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569013/img/mulching_action_landscape_4.png",
  youtubeId: "dQw4w9WgXcQ",
  aspect: "landscape",
  description: "Turning a dense cedar thicket into usable land in hours."
}];
export default function TheMulchingVault() {
  const [selectedVideo, setSelectedVideo] = useState<VideoItem | null>(null);
  return <section id="the-mulching-vault" className="dark bg-background text-foreground relative overflow-hidden py-24 md:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 flex items-start justify-center overflow-hidden pt-10 opacity-10 select-none">
        <span className="font-serif text-[18vw] font-black leading-none text-transparent [-webkit-text-stroke:2px_var(--color-muted)] whitespace-nowrap">
          THE MULCHING VAULT
        </span>
      </div>

      <div className="container relative z-10 mx-auto px-4">
        {}
        <div className="mb-16 text-center">
          <h2 className="font-serif text-4xl font-bold tracking-tight text-primary md:text-6xl">
            ACTION FOOTAGE
          </h2>
          <p className="text-muted-foreground mx-auto mt-4 max-w-2xl text-lg">
            Witness the raw power of our Kubota SVL97-2 and forestry mulcher transforming East Texas properties.
          </p>
        </div>

        {}
        <div className="columns-1 gap-8 md:columns-2 lg:gap-10 space-y-8">
          {VIDEOS.map((video, i) => <div key={video.id} data-index={i} className="group break-inside-avoid relative mb-8 overflow-hidden rounded-xl bg-card shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:shadow-primary/20">
              {}
              <div className={cn("relative w-full overflow-hidden bg-muted", video.aspect === "portrait" ? "aspect-[9/16]" : "aspect-video")}>
                <img src={video.thumbnail} alt={video.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                
                {}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-60 transition-opacity duration-300 group-hover:opacity-80" />

                {}
                <div className="absolute inset-0 flex items-center justify-center">
                  <Button variant="default" size="icon" onClick={() => setSelectedVideo(video)} className="h-20 w-20 rounded-full bg-primary/90 text-primary-foreground shadow-lg backdrop-blur-sm transition-all duration-300 hover:scale-110 hover:bg-primary group-hover:h-24 group-hover:w-24 cursor-pointer [&_svg]:size-8 md:[&_svg]:size-10" aria-label={`Play video: ${video.title}`}>
                    <Play className="ml-1 fill-current" />
                  </Button>
                </div>

                {}
                <div className="absolute inset-x-0 bottom-0 p-6 translate-y-4 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                  <div className="mb-2 flex items-center gap-2 text-primary">
                    <MapPin className="h-4 w-4" />
                    <span className="text-sm font-bold uppercase tracking-wider">
                      {video.location}
                    </span>
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-white mb-2">
                    {video.title}
                  </h3>
                  <p className="text-sm text-gray-300 line-clamp-2">
                    {video.description}
                  </p>
                </div>
              </div>
            </div>)}
        </div>

        {}
        <div className="mt-20 flex flex-col items-center justify-center gap-6 text-center">
          <h3 className="text-2xl font-bold text-foreground">
            Ready to clear your land?
          </h3>
          <Link to="/GetAQuotePage">
            <Button size="lg" className="h-14 px-10 text-lg font-bold uppercase tracking-wide shadow-xl shadow-primary/20">
              Get A Free Estimate
            </Button>
          </Link>
        </div>
      </div>

      {}
      <Dialog open={!!selectedVideo} onOpenChange={open => !open && setSelectedVideo(null)}>
        <DialogContent className="max-w-5xl border-border bg-black p-0 shadow-2xl sm:max-w-[90vw] md:max-w-4xl overflow-hidden">
          <DialogTitle className="sr-only">
            {selectedVideo?.title || "Video Player"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Playing video: {selectedVideo?.title}
          </DialogDescription>
          
          <div className="relative aspect-video w-full bg-black">
            {selectedVideo && <iframe width="100%" height="100%" src={`https://www.youtube.com/embed/${selectedVideo.youtubeId}?autoplay=1&rel=0`} title={selectedVideo.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute inset-0 h-full w-full border-0" />}
            <DialogClose className="absolute top-4 right-4 rounded-full bg-black/50 p-2 text-white hover:bg-black/80 focus:outline-none focus:ring-2 focus:ring-primary">
              <X className="h-6 w-6" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>
          
          {selectedVideo && <div className="bg-card p-6">
              <h3 className="font-serif text-xl font-bold text-foreground">
                {selectedVideo.title}
              </h3>
              <p className="text-muted-foreground mt-2">
                {selectedVideo.description}
              </p>
              <div className="mt-4 flex items-center gap-2 text-sm text-primary">
                <MapPin className="h-4 w-4" />
                <span>{selectedVideo.location}</span>
              </div>
            </div>}
        </DialogContent>
      </Dialog>
    </section>;
}